import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, CheckCircle, MapPin } from 'lucide-react';
import LocationPermissionHint from './LocationPermissionHint';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/field`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/Toronto' }) : '';

const getGPS = () => new Promise((resolve) => {
  if (!navigator.geolocation) { resolve({ gps_status: 'no_device' }); return; }
  navigator.geolocation.getCurrentPosition(
    (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy_m: pos.coords.accuracy, gps_status: 'captured' }),
    (err) => resolve({ gps_status: err.code === 1 ? 'denied' : err.code === 2 ? 'unavailable' : 'timeout' }),
    { timeout: 10000, enableHighAccuracy: true, maximumAge: 30000 }
  );
});

const ackLabel = (ev) => {
  if (ev.ack_on_site === true) return 'on site';
  if (ev.ack_distance_m != null) return `${ev.ack_distance_m >= 1000 ? (ev.ack_distance_m / 1000).toFixed(1) + ' km' : ev.ack_distance_m + ' m'} from the ${ev.ack_ref === 'site' ? 'job site' : 'foreman'}`;
  if (ev.ack_lat != null) return 'location logged';
  return ev.ack_gps_status === 'denied' ? 'location was blocked' : 'no GPS fix';
};

// "A foreman clocked you in" card — employee acknowledges, GPS gets tagged on the clock event.
export default function ClockInAckCard({ onAcked }) {
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(null);
  const [done, setDone] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/clock-ack/pending`, auth()).then(r => setPending(r.data.pending || [])).catch(() => setPending([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const acknowledge = async (ev) => {
    setBusy(ev.id);
    const gps = await getGPS();
    try {
      const r = await axios.post(`${API}/clock-ack/${ev.id}`, gps, auth());
      setDone(r.data);
      setPending(p => p.filter(x => x.id !== ev.id));
      toast.success(`Acknowledged — ${ackLabel(r.data)}`);
      onAcked?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not acknowledge — try again');
    }
    setBusy(null);
  };

  if (pending.length === 0) {
    if (!done) return null;
    return (
      <div className="rounded-xl border border-emerald-300 bg-white border-l-4 border-l-emerald-600 shadow-sm p-3 flex items-center gap-2 text-sm text-emerald-900" data-testid="clock-ack-done">
        <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
        Acknowledged at {fmtTime(done.ack_at)} · {ackLabel(done)}. Remember to clock out at the end of your day.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {pending.map(ev => (
        <div key={ev.id} data-testid={`clock-ack-card-${ev.id}`} className="rounded-xl border border-sky-300 bg-white border-l-4 border-l-sky-600 shadow-sm p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <Clock className="w-7 h-7 shrink-0 text-sky-700" />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-slate-900" data-testid="clock-ack-title">
              {ev.clocked_in_by} clocked you in on Ops at {fmtTime(ev.clock_in_time)}
            </p>
            <p className="text-xs text-slate-600">
              WO <span className="font-mono text-sky-800 font-semibold">{ev.work_order_number || '—'}</span>{ev.customer ? ` · ${ev.customer}` : ''} — remember to clock out at the end of your day.
            </p>
            <LocationPermissionHint className="mt-1.5" />
          </div>
          <button onClick={() => acknowledge(ev)} disabled={busy === ev.id} data-testid={`clock-ack-btn-${ev.id}`}
            className="shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-500 disabled:bg-slate-300 shadow-sm">
            <MapPin className="w-4 h-4" /> {busy === ev.id ? 'Getting location…' : 'Acknowledge'}
          </button>
        </div>
      ))}
    </div>
  );
}
